import React, { useState } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";

const PREFERENCE_OPTIONS = ["Prefer", "Neutral", "Do Not Prefer"];

const PREFER_MAYA_REASONS = [
  "User-Friendly",
  "Fast Transaction",
  "Reliable",
  "No competing terminal available",
  "Better customer support",
  "Better settlement time",
  "Better transaction success rate",
  "Lower fees",
  "Required by company/head office",
  "Customers prefer Maya",
  "Other",
];

const PREFERRED_TERMINALS = [
  "GCash Terminal/SQR",
  "BDO Terminal",
  "Metrobank Terminal",
  "Others",
];

const NOT_PREFER_MAYA_REASONS = [
  "No Installment Options",
  "Inconsistent Aftersales Support",
  "HO Restriction",
  "Slow transactions",
  "Frequent technical issues",
  "Poor signal/connectivity",
  "Difficult to use",
  "High fees",
  "Prefer another provider",
  "Head office requires another terminal",
  "Poor customer support",
  "Lack of features",
  "Other",
];

const CHOOSE_PREFERRED_REASONS = [
  "User-friendly",
  "Fast transactions",
  "Reliable",
  "No competing terminal available",
  "Better customer support",
  "Better settlement time",
  "Better transaction success rate",
  "Lower fees",
  "Required by company/head office",
  "Customers prefer Maya",
  "Other",
];

const INFLUENCE_PREFER_MAYA = [
  "Lower transaction fees",
  "Faster transaction processing",
  "Faster settlement of funds",
  "User-friendly",
  "More reliable with fewer technical issues",
  "Better customer support",
  "Better internet/signal performance",
  "Free or discounted terminal",
  "More promotions or incentives",
  "More features for my business (e.g., reports, inventory, financing)",
  "More customers prefer paying with Maya",
  "Approval or recommendation from head office/management",
  "Better training or onboarding",
  "Others",
];

const BRANCH_RESET = {
  Prefer: {
    preferred_payment_terminal: "",
    preferred_payment_terminal_other: "",
    not_prefer_maya_reasons: [],
    not_prefer_maya_other: "",
    choose_preferred_terminal_reasons: [],
    choose_preferred_terminal_other: "",
    influence_prefer_maya: [],
    influence_prefer_maya_other: "",
  },
  Neutral: {
    prefer_maya_reasons: [],
    prefer_maya_other: "",
    preferred_payment_terminal: "",
    preferred_payment_terminal_other: "",
    not_prefer_maya_reasons: [],
    not_prefer_maya_other: "",
    choose_preferred_terminal_reasons: [],
    choose_preferred_terminal_other: "",
  },
  "Do Not Prefer": {
    prefer_maya_reasons: [],
    prefer_maya_other: "",
    influence_prefer_maya: [],
    influence_prefer_maya_other: "",
  },
};

function MayaMerchantSurvey({ navigation, primaryDetails, setPrimaryDetails }) {
  const [isError, setIsError] = useState({});

  const preference = primaryDetails.maya_terminal_preference || "";

  const handlePreferenceChange = (e) => {
    const { value } = e.target;
    setPrimaryDetails((prev) => ({
      ...prev,
      maya_terminal_preference: value,
      ...(BRANCH_RESET[value] || {}),
    }));
    if (isError.maya_terminal_preference) {
      setIsError((prev) => ({ ...prev, maya_terminal_preference: false }));
    }
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setPrimaryDetails((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "preferred_payment_terminal" && value !== "Others"
        ? { preferred_payment_terminal_other: "" }
        : {}),
    }));
    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setPrimaryDetails((prev) => {
      const currentValues = prev[name] || [];
      const nextValues = checked
        ? [...currentValues, value]
        : currentValues.filter((item) => item !== value);

      const updates = { [name]: nextValues };

      if (!checked) {
        if (name === "prefer_maya_reasons" && value === "Other") {
          updates.prefer_maya_other = "";
        }
        if (name === "not_prefer_maya_reasons" && value === "Other") {
          updates.not_prefer_maya_other = "";
        }
        if (name === "choose_preferred_terminal_reasons" && value === "Other") {
          updates.choose_preferred_terminal_other = "";
        }
        if (name === "influence_prefer_maya" && value === "Others") {
          updates.influence_prefer_maya_other = "";
        }
      }

      return { ...prev, ...updates };
    });

    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setPrimaryDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError((prev) => ({ ...prev, [name]: false }));
    }
  };

  const hasOtherText = (selected, optionLabel, textValue) => {
    if (!(selected || []).includes(optionLabel)) return true;
    return !!(textValue && textValue.trim());
  };

  const validateForm = () => {
    const errors = {};

    if (!preference) {
      errors.maya_terminal_preference = true;
    }

    if (preference === "Prefer") {
      const reasons = primaryDetails.prefer_maya_reasons || [];
      if (reasons.length === 0) errors.prefer_maya_reasons = true;
      if (!hasOtherText(reasons, "Other", primaryDetails.prefer_maya_other)) {
        errors.prefer_maya_other = true;
      }
    }

    if (preference === "Neutral") {
      const reasons = primaryDetails.influence_prefer_maya || [];
      if (reasons.length === 0) errors.influence_prefer_maya = true;
      if (!hasOtherText(reasons, "Others", primaryDetails.influence_prefer_maya_other)) {
        errors.influence_prefer_maya_other = true;
      }
    }

    if (preference === "Do Not Prefer") {
      if (!primaryDetails.preferred_payment_terminal) {
        errors.preferred_payment_terminal = true;
      }
      if (
        primaryDetails.preferred_payment_terminal === "Others" &&
        !(primaryDetails.preferred_payment_terminal_other || "").trim()
      ) {
        errors.preferred_payment_terminal_other = true;
      }

      const notPrefer = primaryDetails.not_prefer_maya_reasons || [];
      if (notPrefer.length === 0) errors.not_prefer_maya_reasons = true;
      if (!hasOtherText(notPrefer, "Other", primaryDetails.not_prefer_maya_other)) {
        errors.not_prefer_maya_other = true;
      }

      const choosePreferred = primaryDetails.choose_preferred_terminal_reasons || [];
      if (choosePreferred.length === 0) errors.choose_preferred_terminal_reasons = true;
      if (
        !hasOtherText(
          choosePreferred,
          "Other",
          primaryDetails.choose_preferred_terminal_other
        )
      ) {
        errors.choose_preferred_terminal_other = true;
      }
    }

    setIsError(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please complete all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    if (!preference) return false;

    if (preference === "Prefer") {
      const reasons = primaryDetails.prefer_maya_reasons || [];
      if (reasons.length === 0) return false;
      return hasOtherText(reasons, "Other", primaryDetails.prefer_maya_other);
    }

    if (preference === "Neutral") {
      const reasons = primaryDetails.influence_prefer_maya || [];
      if (reasons.length === 0) return false;
      return hasOtherText(reasons, "Others", primaryDetails.influence_prefer_maya_other);
    }

    if (preference === "Do Not Prefer") {
      if (!primaryDetails.preferred_payment_terminal) return false;
      if (
        primaryDetails.preferred_payment_terminal === "Others" &&
        !(primaryDetails.preferred_payment_terminal_other || "").trim()
      ) {
        return false;
      }

      const notPrefer = primaryDetails.not_prefer_maya_reasons || [];
      if (notPrefer.length === 0) return false;
      if (!hasOtherText(notPrefer, "Other", primaryDetails.not_prefer_maya_other)) {
        return false;
      }

      const choosePreferred = primaryDetails.choose_preferred_terminal_reasons || [];
      if (choosePreferred.length === 0) return false;
      return hasOtherText(
        choosePreferred,
        "Other",
        primaryDetails.choose_preferred_terminal_other
      );
    }

    return false;
  };

  const handleNext = () => {
    if (validateForm()) {
      navigation.go("visibility-photo");
    }
  };

  const handleBack = () => {
    navigation.previous();
  };

  const renderCheckboxGroup = (name, options, errorKey) => (
    <div className="form-checkbox-group">
      {options.map((option) => {
        const selected = (primaryDetails[name] || []).includes(option);
        return (
          <div
            key={`${name}-${option}`}
            className={`form-checkbox-item${selected ? " selected" : ""}`}
          >
            <div className="form-checkbox-flex">
              <Form.Check
                type="checkbox"
                name={name}
                value={option}
                label={option}
                checked={selected}
                onChange={handleCheckboxChange}
                className="form-checkbox"
              />
            </div>
          </div>
        );
      })}
      {isError[errorKey] && <div className="form-error">Please select at least one option</div>}
    </div>
  );

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">MAYA MERCHANT SURVEY</h4>
              <p className="form-desc">Please answer based on the merchant&apos;s preference</p>
            </div>

            <div className="form-main">
              <div className="form-main-content">
                <Form className="form-form">
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      How would you describe your preference for the Maya Terminal?
                      <span className="form-required">*</span>
                    </Form.Label>
                    <div className="form-radio-group">
                      {PREFERENCE_OPTIONS.map((option) => (
                        <div
                          key={option}
                          className={`form-radio-item${preference === option ? " selected" : ""}`}
                        >
                          <div className="form-radio-flex">
                            <Form.Check
                              type="radio"
                              name="maya_terminal_preference"
                              value={option}
                              label={option}
                              checked={preference === option}
                              onChange={handlePreferenceChange}
                              className="form-radio"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {isError.maya_terminal_preference && (
                      <div className="form-error">Please select an option</div>
                    )}
                  </Form.Group>

                  {preference === "Prefer" && (
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        Why Does the merchant prefer the Maya Terminal?
                        <span className="form-required">*</span>
                      </Form.Label>
                      {renderCheckboxGroup(
                        "prefer_maya_reasons",
                        PREFER_MAYA_REASONS,
                        "prefer_maya_reasons"
                      )}
                      {(primaryDetails.prefer_maya_reasons || []).includes("Other") && (
                        <Form.Control
                          type="text"
                          name="prefer_maya_other"
                          value={primaryDetails.prefer_maya_other || ""}
                          onChange={handleTextChange}
                          placeholder="Please specify"
                          className={`form-input mt-2${isError.prefer_maya_other ? " form-input-error" : ""}`}
                        />
                      )}
                      {isError.prefer_maya_other && (
                        <div className="form-error">Please specify Other</div>
                      )}
                    </Form.Group>
                  )}

                  {preference === "Neutral" && (
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        What would influence the merchant to prefer Maya Terminals?
                        <span className="form-required">*</span>
                      </Form.Label>
                      {renderCheckboxGroup(
                        "influence_prefer_maya",
                        INFLUENCE_PREFER_MAYA,
                        "influence_prefer_maya"
                      )}
                      {(primaryDetails.influence_prefer_maya || []).includes("Others") && (
                        <Form.Control
                          type="text"
                          name="influence_prefer_maya_other"
                          value={primaryDetails.influence_prefer_maya_other || ""}
                          onChange={handleTextChange}
                          placeholder="Please specify"
                          className={`form-input mt-2${isError.influence_prefer_maya_other ? " form-input-error" : ""}`}
                        />
                      )}
                      {isError.influence_prefer_maya_other && (
                        <div className="form-error">Please specify Others</div>
                      )}
                    </Form.Group>
                  )}

                  {preference === "Do Not Prefer" && (
                    <>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          What is the merchant&apos;s preferred payment terminal?
                          <span className="form-required">*</span>
                        </Form.Label>
                        <div className="form-radio-group">
                          {PREFERRED_TERMINALS.map((option) => (
                            <div
                              key={option}
                              className={`form-radio-item${
                                primaryDetails.preferred_payment_terminal === option
                                  ? " selected"
                                  : ""
                              }`}
                            >
                              <div className="form-radio-flex">
                                <Form.Check
                                  type="radio"
                                  name="preferred_payment_terminal"
                                  value={option}
                                  label={option}
                                  checked={
                                    primaryDetails.preferred_payment_terminal === option
                                  }
                                  onChange={handleRadioChange}
                                  className="form-radio"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        {isError.preferred_payment_terminal && (
                          <div className="form-error">Please select an option</div>
                        )}
                        {primaryDetails.preferred_payment_terminal === "Others" && (
                          <Form.Control
                            type="text"
                            name="preferred_payment_terminal_other"
                            value={primaryDetails.preferred_payment_terminal_other || ""}
                            onChange={handleTextChange}
                            placeholder="Please specify"
                            className={`form-input mt-2${
                              isError.preferred_payment_terminal_other
                                ? " form-input-error"
                                : ""
                            }`}
                          />
                        )}
                        {isError.preferred_payment_terminal_other && (
                          <div className="form-error">Please specify Others</div>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Why doesn&apos;t the merchant prefer the Maya Terminal?
                          <span className="form-required">*</span>
                        </Form.Label>
                        {renderCheckboxGroup(
                          "not_prefer_maya_reasons",
                          NOT_PREFER_MAYA_REASONS,
                          "not_prefer_maya_reasons"
                        )}
                        {(primaryDetails.not_prefer_maya_reasons || []).includes("Other") && (
                          <Form.Control
                            type="text"
                            name="not_prefer_maya_other"
                            value={primaryDetails.not_prefer_maya_other || ""}
                            onChange={handleTextChange}
                            placeholder="Please specify"
                            className={`form-input mt-2${
                              isError.not_prefer_maya_other ? " form-input-error" : ""
                            }`}
                          />
                        )}
                        {isError.not_prefer_maya_other && (
                          <div className="form-error">Please specify Other</div>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Why does the merchant choose their preferred Terminal?
                          <span className="form-required">*</span>
                        </Form.Label>
                        {renderCheckboxGroup(
                          "choose_preferred_terminal_reasons",
                          CHOOSE_PREFERRED_REASONS,
                          "choose_preferred_terminal_reasons"
                        )}
                        {(primaryDetails.choose_preferred_terminal_reasons || []).includes(
                          "Other"
                        ) && (
                          <Form.Control
                            type="text"
                            name="choose_preferred_terminal_other"
                            value={primaryDetails.choose_preferred_terminal_other || ""}
                            onChange={handleTextChange}
                            placeholder="Please specify"
                            className={`form-input mt-2${
                              isError.choose_preferred_terminal_other
                                ? " form-input-error"
                                : ""
                            }`}
                          />
                        )}
                        {isError.choose_preferred_terminal_other && (
                          <div className="form-error">Please specify Other</div>
                        )}
                      </Form.Group>
                    </>
                  )}

                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default MayaMerchantSurvey;
